import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { formatDateTime } from '../utils/dates';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const KIND_LABELS = {
  maintenance: 'Maintenance requests',
  tenant: 'Tenants',
  tenancy: 'Tenancies',
  property: 'Properties',
  contractor: 'Contractors',
  lead: 'Leads',
  case: 'Section 8 cases',
  case_document: 'Case documents',
  interaction: 'Interactions',
};

/**
 * 2026-02 FIX 3 — Settings → Data Management.
 *
 * Surfaces every archived record across every supported kind so the
 * agent can Restore or Delete Now from one place. Cases, CaseDocuments
 * and Interactions are legal audit records — `is_deletable=false` and
 * the backend rejects any hard-delete request.
 */
export default function ArchivedRecordsPanel() {
  const [groups, setGroups] = useState({});
  const [cutoff, setCutoff] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState({});
  // shape: { [kind]: Set(ids) }

  const reload = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/archive/list-all`, { credentials: 'include' });
      if (!res.ok) throw new Error('Could not load archived records');
      const data = await res.json();
      setGroups(data.groups || {});
      setCutoff(data.cutoff_days || 30);
      setSelected({});
    } catch (e) {
      setError(e.message || 'Could not load archived records.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const toggleSelect = (kind, id) => {
    setSelected(prev => {
      const set = new Set(prev[kind] || []);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...prev, [kind]: set };
    });
  };
  const selectAll = (kind, ids) => {
    setSelected(prev => ({ ...prev, [kind]: new Set(ids) }));
  };
  const clearSelect = (kind) => {
    setSelected(prev => ({ ...prev, [kind]: new Set() }));
  };

  const restoreOne = async (kind, id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/archive/${kind}/${id}/restore`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error();
      await reload();
    } catch {
      // eslint-disable-next-line no-alert
      window.alert('Could not restore. Please try again.');
    }
  };

  const deleteOne = async (kind, id, label) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Permanently delete this record now?\n\n"${label}"\n\nThis bypasses the 30-day grace period and cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/archive/${kind}/${id}/hard`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Delete failed');
      }
      await reload();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e.message || 'Could not delete. Legal audit records cannot be hard-deleted.');
    }
  };

  const bulkRestore = async (kind) => {
    const ids = Array.from(selected[kind] || []);
    if (ids.length === 0) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/archive/${kind}/bulk-restore`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new Error();
      await reload();
    } catch {
      // eslint-disable-next-line no-alert
      window.alert('Bulk restore failed.');
    }
  };

  const bulkDelete = async (kind) => {
    const ids = Array.from(selected[kind] || []);
    if (ids.length === 0) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Permanently delete ${ids.length} ${KIND_LABELS[kind] || kind} record(s) NOW?\n\nThis bypasses the 30-day grace period and cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/archive/${kind}/bulk-hard-delete`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Delete failed');
      }
      await reload();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e.message || 'Bulk delete failed. Legal audit records cannot be hard-deleted.');
    }
  };

  const totalArchived = useMemo(
    () => Object.values(groups).reduce((sum, g) => sum + (g.count || 0), 0),
    [groups],
  );

  return (
    <div data-testid="data-management-panel"
         style={{ padding: '1rem 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.35rem', color: '#0f172a' }}>
            Archived Records
          </h2>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.92rem' }}>
            Records archived from your main views. Items with a 30-day countdown will be
            permanently removed automatically — Section&nbsp;8 cases, case documents and
            interactions are <strong>legal audit records</strong> and stay archived
            indefinitely (they cannot be deleted).
          </p>
        </div>
        <button type="button" onClick={reload}
                data-testid="btn-refresh-archived"
                style={{ background: '#f1f5f9', color: '#0f172a',
                         border: '1px solid #cbd5e1', borderRadius: 6,
                         padding: '0.45rem 0.95rem', fontWeight: 600,
                         cursor: 'pointer', fontSize: '0.85rem' }}>
          Refresh
        </button>
      </div>

      {loading && <div style={{ color: '#64748b', padding: '2rem 0' }}>Loading…</div>}
      {error && (
        <div data-testid="dm-error"
             style={{ color: '#b91c1c', background: '#fef2f2',
                      border: '1px solid #fecaca', padding: '0.7rem 0.9rem',
                      borderRadius: 8 }}>
          {error}
        </div>
      )}

      {!loading && !error && totalArchived === 0 && (
        <div data-testid="dm-empty"
             style={{ background: '#f8fafc', padding: '2rem',
                      borderRadius: 12, textAlign: 'center',
                      color: '#64748b' }}>
          You have no archived records.
        </div>
      )}

      {!loading && !error && Object.entries(groups).map(([kind, group]) => {
        if (!group.count) return null;
        const selCount = (selected[kind] || new Set()).size;
        const allIds = group.rows.map(r => r.id);
        return (
          <section key={kind}
                   data-testid={`dm-group-${kind}`}
                   style={{ background: '#fff', border: '1px solid #e2e8f0',
                            borderRadius: 12, marginBottom: 16,
                            overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', padding: '0.9rem 1.1rem',
                          background: '#f8fafc',
                          borderBottom: '1px solid #e2e8f0' }}>
              <div>
                <strong style={{ color: '#0f172a' }}>
                  {KIND_LABELS[kind] || kind}
                </strong>
                <span style={{ marginLeft: 8, color: '#64748b',
                               fontSize: '0.85rem' }}>
                  {group.count} archived
                </span>
                {!group.is_deletable_default && (
                  <span style={{ marginLeft: 8,
                                  background: '#fef3c7', color: '#92400e',
                                  fontSize: '0.7rem', padding: '0.15rem 0.5rem',
                                  borderRadius: 999, fontWeight: 600 }}>
                    Legal — retain forever
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ color: '#64748b', fontSize: '0.8rem' }}>
                  {selCount > 0 ? `${selCount} selected` : ''}
                </span>
                <button type="button"
                        onClick={() => selCount === group.count ? clearSelect(kind) : selectAll(kind, allIds)}
                        data-testid={`dm-select-all-${kind}`}
                        style={{ background: '#e2e8f0', color: '#0f172a',
                                 border: 'none', borderRadius: 6,
                                 padding: '0.3rem 0.7rem', fontWeight: 600,
                                 fontSize: '0.78rem', cursor: 'pointer' }}>
                  {selCount === group.count ? 'Clear' : 'Select all'}
                </button>
                <button type="button"
                        onClick={() => bulkRestore(kind)}
                        disabled={selCount === 0}
                        data-testid={`dm-bulk-restore-${kind}`}
                        style={{ background: selCount === 0 ? '#e2e8f0' : '#0073EA',
                                 color: selCount === 0 ? '#94a3b8' : '#fff',
                                 border: 'none', borderRadius: 6,
                                 padding: '0.3rem 0.85rem', fontWeight: 600,
                                 fontSize: '0.78rem',
                                 cursor: selCount === 0 ? 'not-allowed' : 'pointer' }}>
                  Bulk restore
                </button>
                {group.is_deletable_default && (
                  <button type="button"
                          onClick={() => bulkDelete(kind)}
                          disabled={selCount === 0}
                          data-testid={`dm-bulk-delete-${kind}`}
                          style={{ background: selCount === 0 ? '#e2e8f0' : '#b91c1c',
                                   color: selCount === 0 ? '#94a3b8' : '#fff',
                                   border: 'none', borderRadius: 6,
                                   padding: '0.3rem 0.85rem', fontWeight: 600,
                                   fontSize: '0.78rem',
                                   cursor: selCount === 0 ? 'not-allowed' : 'pointer' }}>
                    Bulk delete now
                  </button>
                )}
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse',
                            fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ color: '#475569', textAlign: 'left',
                             borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ width: 36, padding: '0.55rem 1rem' }}></th>
                  <th style={{ padding: '0.55rem 0.6rem' }}>Record</th>
                  <th style={{ padding: '0.55rem 0.6rem' }}>Archived</th>
                  <th style={{ padding: '0.55rem 0.6rem' }}>Days remaining</th>
                  <th style={{ padding: '0.55rem 0.6rem', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {group.rows.map(r => {
                  const isSel = (selected[kind] || new Set()).has(r.id);
                  return (
                    <tr key={r.id}
                        data-testid={`dm-row-${kind}-${r.id}`}
                        style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.55rem 1rem' }}>
                        <input type="checkbox"
                               checked={isSel}
                               onChange={() => toggleSelect(kind, r.id)}
                               data-testid={`dm-check-${kind}-${r.id}`} />
                      </td>
                      <td style={{ padding: '0.55rem 0.6rem', color: '#0f172a',
                                   fontWeight: 600 }}>
                        {r.label || r.id}
                      </td>
                      <td style={{ padding: '0.55rem 0.6rem', color: '#475569' }}>
                        {r.archived_at ? formatDateTime(r.archived_at) : '—'}
                      </td>
                      <td style={{ padding: '0.55rem 0.6rem' }}>
                        {r.is_deletable ? (
                          <span style={{
                            color: (r.days_remaining ?? cutoff) <= 3 ? '#b91c1c' : '#475569',
                            fontWeight: 600,
                          }}>
                            {r.days_remaining ?? '—'} days
                          </span>
                        ) : (
                          <span style={{ color: '#92400e', fontSize: '0.78rem' }}>
                            Retained forever
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right' }}>
                        <button type="button"
                                onClick={() => restoreOne(kind, r.id)}
                                data-testid={`dm-restore-${kind}-${r.id}`}
                                style={{ background: '#ecfeff', color: '#0369a1',
                                         border: '1px solid #bae6fd', borderRadius: 6,
                                         padding: '0.3rem 0.7rem', fontWeight: 600,
                                         fontSize: '0.75rem', cursor: 'pointer' }}>
                          Restore
                        </button>
                        {r.is_deletable && (
                          <button type="button"
                                  onClick={() => deleteOne(kind, r.id, r.label)}
                                  data-testid={`dm-delete-now-${kind}-${r.id}`}
                                  style={{ background: '#fef2f2', color: '#b91c1c',
                                           border: '1px solid #fecaca', borderRadius: 6,
                                           padding: '0.3rem 0.7rem', fontWeight: 600,
                                           fontSize: '0.75rem', cursor: 'pointer',
                                           marginLeft: 6 }}>
                            Delete now
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        );
      })}
    </div>
  );
}
